/**
 * Adapter from the `/api/v1/charts/:id` positions to the data `ChartView` draws.
 */

import type { ChartPlanet, ChartHouse, ChartAngle, ChartAngles, ChartAspect } from '../../hooks/useChart';
import type { ChartPositions } from '../../hooks/useCharts';

export interface ChartViewData {
  planets: ChartPlanet[];
  houses?: ChartHouse[];
  angles?: ChartAngles;
  aspects?: ChartAspect[];
}

/** aquarius → Aquarius: the `me:natalChart.names.signs.*` and glossary keys are capitalised,
 *  while the server sends lowercase signs. Idempotent. */
export function capitalizeSign(sign: string): string {
  return sign ? sign.charAt(0).toUpperCase() + sign.slice(1) : sign;
}

function angle(a: ChartAngle): ChartAngle {
  return { ...a, sign: capitalizeSign(a.sign) };
}

/** Capitalises every sign string of the chart data (planets, houses, angles). Pure and idempotent. */
export function normalizeChartViewData(d: ChartViewData): ChartViewData {
  const a = d.angles;
  return {
    planets: d.planets.map((pl) => ({ ...pl, sign: capitalizeSign(pl.sign) })),
    houses: d.houses?.map((h) => ({ ...h, sign: capitalizeSign(h.sign) })),
    angles: a
      ? {
          asc: angle(a.asc), mc: angle(a.mc), ic: angle(a.ic), dc: angle(a.dc),
          ...(a.vertex ? { vertex: angle(a.vertex) } : {}),
        }
      : undefined,
    aspects: d.aspects,
  };
}

/**
 * `/charts/:id` positions → ChartViewData. Capitalises sign strings, fills a missing planet
 * `house` with 0 (the tables show «—» for it) and returns null when positions or planets are missing.
 */
export function toChartViewData(p: ChartPositions | null): ChartViewData | null {
  if (!p || !p.planets || p.planets.length === 0) return null;
  return normalizeChartViewData({
    planets: p.planets.map((pl) => ({ ...pl, house: pl.house ?? 0 })),
    houses: p.houses,
    angles: p.angles,
    aspects: p.aspects,
  });
}
